import { useQuery } from '@tanstack/react-query'
import { historyService } from '@/lib/api/services/history'
import type { HistoryFilterParams, PaginationParams } from '@/lib/api/types'

// Query keys factory
export const historyKeys = {
  all: ['histories'] as const,
  lists: () => [...historyKeys.all, 'list'] as const,
  list: (filters: HistoryFilterParams) => [...historyKeys.lists(), filters] as const,
  searches: () => [...historyKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...historyKeys.searches(), searchTerm, params] as const,
  details: () => [...historyKeys.all, 'detail'] as const,
  detail: (id: string) => [...historyKeys.details(), id] as const,
}

/**
 * Hook to fetch histories with filters
 */
export const useHistories = (filters: HistoryFilterParams = {}) => {
  return useQuery({
    queryKey: historyKeys.list(filters),
    queryFn: () => historyService.filterHistories(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to search histories with pagination
 */
export const useSearchHistories = (
  searchTerm: string,
  params: PaginationParams = { page: 1, size: 10 }
) => {
  return useQuery({
    queryKey: historyKeys.search(searchTerm, params),
    queryFn: () => historyService.searchHistories(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to fetch a single history by ID
 */
export const useHistory = (historyId: string | undefined) => {
  return useQuery({
    queryKey: historyKeys.detail(historyId || ''),
    queryFn: () => historyService.getHistoryById(historyId!),
    enabled: !!historyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

